import React, { useState, useCallback, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { MapPin, X, Check } from 'lucide-react';
import _translate from "i18n";
import Swal from "sweetalert2";
import { useMapStore } from "store/mapStore";

interface CoordinatesFormData {
  latitude: string;
  longitude: string;
}

const GoToCoordinatesButton: React.FC = () => {
  const { countryCoordinates, setGoToCoordinates } = useMapStore();
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState<CoordinatesFormData>({
    latitude: countryCoordinates?.center?.lat?.toString() || '',
    longitude: countryCoordinates?.center?.lng?.toString() || ''
  });

  useEffect(() => {
    setFormData({
      latitude: countryCoordinates?.center?.lat?.toString() || '',
      longitude: countryCoordinates?.center?.lng?.toString() || ''
    });
  }, [countryCoordinates]);
  const [errors, setErrors] = useState<Partial<CoordinatesFormData>>({});

  const validateCoordinates = useCallback((data: CoordinatesFormData): boolean => {
    const newErrors: Partial<CoordinatesFormData> = {};

    // Validate latitude
    const lat = parseFloat(data.latitude);
    if (!data.latitude.trim()) {
      newErrors.latitude = _translate('map.coordinates.validation.latitudeRequired');
    } else if (isNaN(lat)) {
      newErrors.latitude = _translate('map.coordinates.validation.latitudeInvalid');
    } else if (lat < -90 || lat > 90) {
      newErrors.latitude = _translate('map.coordinates.validation.latitudeRange');
    }

    // Validate longitude
    const lng = parseFloat(data.longitude);
    if (!data.longitude.trim()) {
      newErrors.longitude = _translate('map.coordinates.validation.longitudeRequired');
    } else if (isNaN(lng)) {
      newErrors.longitude = _translate('map.coordinates.validation.longitudeInvalid');
    } else if (lng < -180 || lng > 180) {
      newErrors.longitude = _translate('map.coordinates.validation.longitudeRange');
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }, []);

  const handleInputChange = useCallback((field: keyof CoordinatesFormData, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: undefined }));
    }
  }, [errors]);

  const handleAccept = useCallback(() => {
    if (!validateCoordinates(formData)) return;

    const lat = parseFloat(formData.latitude);
    const lng = parseFloat(formData.longitude);

    setGoToCoordinates({ lat, lng });

    // Close form and reset
    setShowForm(false);
    setFormData({ latitude: '', longitude: '' });
    setErrors({});

    // Optionally, show a success message or log the coordinates
    Swal.fire({
      title: _translate('common.success'),
      text: _translate('map.coordinates.successMessage').replace('{{latitude}}', formData.latitude).replace('{{longitude}}', formData.longitude),
      icon: 'success',
      confirmButtonColor: '#10B981'
    });
  }, [formData, validateCoordinates, setGoToCoordinates]);

  const handleCancel = useCallback(() => {
    setShowForm(false);
    setFormData({ latitude: '', longitude: '' });
    setErrors({});
  }, []);

  const handleOpenForm = useCallback(() => {
    setShowForm(true);
  }, []);

  return (
    <>
      <button
        onClick={handleOpenForm}
        className="px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg shadow-sm transition-colors flex items-center gap-2 font-medium text-sm"
        title={_translate('map.coordinates.goToCoordinates')}
      >
        <MapPin className="w-4 h-4" />
        <span>{_translate('map.coordinates.goToPoint')}</span>
      </button>

      {showForm && createPortal(
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[9999] p-4 font-['Inter']">
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-xl max-w-md w-full relative">
            <div className="flex items-center justify-between p-6 border-b dark:border-gray-700">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-emerald-100 dark:bg-emerald-900/30 rounded-lg flex items-center justify-center">
                  <MapPin className="w-5 h-5 text-emerald-600 dark:text-emerald-400" />
                </div>
                <div>
                  <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
                    {_translate('map.coordinates.goToCoordinates')}
                  </h2>
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    {_translate('map.coordinates.enterCoordinates')}
                  </p>
                </div>
              </div>
              <button
                onClick={handleCancel}
                className="text-gray-400 hover:text-gray-500 dark:hover:text-gray-300 transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="p-6 space-y-4">
              <div className="space-y-2">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                  {_translate('latitude')}
                </label>
                <input
                  type="text"
                  value={formData.latitude}
                  onChange={(e) => handleInputChange('latitude', e.target.value)}
                  placeholder="Ej: -33.45694"
                  className={`w-full px-3 py-2 border rounded-lg shadow-sm focus:ring-2 focus:ring-offset-2 dark:bg-gray-700 dark:border-gray-600 dark:text-white ${errors.latitude
                    ? 'border-red-300 focus:ring-red-500 dark:border-red-500'
                    : 'border-gray-300 focus:ring-emerald-500 dark:border-gray-500'
                    }`}
                />
                {errors.latitude && (
                  <p className="text-sm text-red-600 dark:text-red-400">
                    {errors.latitude}
                  </p>
                )}
              </div>

              <div className="space-y-2">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                  {_translate('longitude')}
                </label>
                <input
                  type="text"
                  value={formData.longitude}
                  onChange={(e) => handleInputChange('longitude', e.target.value)}
                  placeholder="Ej: -70.64827"
                  className={`w-full px-3 py-2 border rounded-lg shadow-sm focus:ring-2 focus:ring-offset-2 dark:bg-gray-700 dark:border-gray-600 dark:text-white ${errors.longitude
                    ? 'border-red-300 focus:ring-red-500 dark:border-red-500'
                    : 'border-gray-300 focus:ring-emerald-500 dark:border-gray-500'
                    }`}
                />
                {errors.longitude && (
                  <p className="text-sm text-red-600 dark:text-red-400">
                    {errors.longitude}
                  </p>
                )}
              </div>

              <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-3">
                <p className="text-sm text-blue-700 dark:text-blue-300">
                  <strong>{_translate('format')}:</strong> {_translate('map.coordinates.formatExample')}
                </p>
              </div>
            </div>

            <div className="flex justify-end gap-3 p-6 bg-gray-50 dark:bg-gray-700/50 border-t dark:border-gray-700 rounded-b-xl">
              <button
                onClick={handleCancel}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg shadow-sm hover:bg-gray-50 dark:bg-gray-700 dark:text-gray-300 dark:border-gray-600 dark:hover:bg-gray-600 transition-colors"
              >
                <X className="w-4 h-4 inline mr-2" />
                {_translate('Cancelar')}
              </button>
              <button
                onClick={handleAccept}
                className="px-4 py-2 text-sm font-medium text-white bg-emerald-600 border border-transparent rounded-lg shadow-sm hover:bg-emerald-700 transition-colors flex items-center gap-2"
              >
                <Check className="w-4 h-4" />
                {_translate('map.coordinates.goToPoint')}
              </button>
            </div>
          </div>
        </div>,
        document.getElementById('map-portal-root') || document.body
      )}
    </>
  );
};

export default GoToCoordinatesButton;
